import React, { useCallback, useEffect, useState } from "react";
import { Card, Badge, ButtonGroup, Button, Alert, Spinner } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  thirdPartyHmrcConnectUrlApi,
  thirdPartyHmrcStatusApi,
  thirdPartyHmrcTestApi,
  thirdPartyHmrcDisconnectApi,
  thirdPartyHmrcEnvironmentSetApi,
} from "../../../../helpers/fakebackend_helper";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import HmrcVatReturns from "./HmrcVatReturns";
import HmrcCorporationTax from "./HmrcCorporationTax";

const envLabel = (env) => (env === "live" ? "Live" : "Test (Sandbox)");

// Task 24258 - HMRC connection (VAT / Making Tax Digital, shared with payroll RTI).
//
// Connect sends the browser to HMRC. HMRC returns it to the API's own callback
// (not to this page - HMRC needs a fixed, registered redirect URI and every
// company is on its own subdomain), which swaps the code for tokens and then
// sends the browser back here with ?hmrc=connected or ?hmrc=error&hmrc_message=.
function HmrcConnection() {
  const history = useHistory();
  const location = useLocation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(""); // 'connect' | 'test' | 'disconnect' | 'environment'
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [confirm, setConfirm] = useState(null); // { kind: 'disconnect' } | { kind: 'environment', next }

  const loadStatus = useCallback(async () => {
    try {
      const response = await thirdPartyHmrcStatusApi();
      if (response?.status === 200) setStatus(response);
    } catch (e) {
      // non-fatal - the page still offers Connect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Outcome of the round trip through HMRC, then strip it from the URL so a
  // refresh does not repeat the toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("hmrc");
    if (!outcome) return;

    if (outcome === "connected") {
      toast.success("Connected to HMRC");
    } else {
      toast.error(params.get("hmrc_message") || "The HMRC connection was not completed.");
    }

    params.delete("hmrc");
    params.delete("hmrc_message");
    const rest = params.toString();
    history.replace(`${location.pathname}${rest ? `?${rest}` : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setBusy("connect");
    try {
      const returnUrl = `${window.location.origin}${location.pathname}`;
      const response = await thirdPartyHmrcConnectUrlApi(returnUrl);
      if (response?.status === 200 && response.url_auth) {
        window.location.assign(response.url_auth);
        return; // leaving the page
      }
      toast.error(response?.error || "HMRC connection URL not available.");
    } catch (e) {
      toast.error("HMRC connection URL not available.");
    }
    setBusy("");
  };

  const handleTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const response = await thirdPartyHmrcTestApi();
      setTestResult(
        response?.status === 200
          ? { ok: true, message: response.message }
          : { ok: false, message: response?.error || "HMRC did not accept the test call." }
      );
    } catch (e) {
      setTestResult({ ok: false, message: "CRMit could not reach HMRC." });
    }
    setBusy("");
    loadStatus(); // a test may have refreshed the access token
  };

  const handleDisconnect = async () => {
    setBusy("disconnect");
    try {
      const response = await thirdPartyHmrcDisconnectApi();
      if (response?.status === 200) {
        toast.success("Disconnected from HMRC");
        setTestResult(null);
      } else {
        toast.error(response?.error || "Could not disconnect from HMRC.");
      }
    } catch (e) {
      toast.error("Could not disconnect from HMRC.");
    }
    setBusy("");
    setConfirm(null);
    loadStatus();
  };

  const handleSwitchEnvironment = async (next) => {
    setBusy("environment");
    try {
      const response = await thirdPartyHmrcEnvironmentSetApi([{ Environment: next }]);
      if (response?.status === 200) {
        toast.success(`Switched to ${envLabel(next)} mode. Please reconnect to HMRC.`);
        setTestResult(null);
      } else {
        toast.error(response?.error || "Could not switch HMRC environment.");
      }
    } catch (e) {
      toast.error("Could not switch HMRC environment.");
    }
    setBusy("");
    setConfirm(null);
    loadStatus();
  };

  const requestEnvironment = (next) => {
    if (!status || next === status.environment || busy) return;
    if (next === "live" && !status.live_configured) {
      toast.error(
        "HMRC production credentials are not configured yet. Live mode is unavailable until HMRC grants production access."
      );
      return;
    }
    setConfirm({ kind: "environment", next });
  };

  if (loading) {
    return (
      <Card className="p-2">
        <LoadingSpinner />
      </Card>
    );
  }

  const environment = status?.environment === "live" ? "live" : "test";
  const connected = Boolean(status?.connected);

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h5 className="mb-0">HMRC Integration</h5>
        <Badge color={environment === "live" ? "success" : "warning"}>{envLabel(environment)}</Badge>
      </div>

      <p className="text-muted mt-2 mb-0">
        Connect your HMRC account (Government Gateway) to retrieve VAT obligations and submit returns
        under Making Tax Digital. The same connection is used for payroll RTI submissions.
      </p>

      <hr />

      {/* Connection */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
        <div>
          {connected ? (
            <>
              <div className="fw-semibold text-success">
                <i className="ri-checkbox-circle-fill me-1" />
                Connected to HMRC {envLabel(environment)}
              </div>
              {status?.connected_at && (
                <div className="text-muted small">
                  Since {moment(status.connected_at).format("DD MMM YYYY HH:mm")}
                </div>
              )}
              {status?.refresh_token_expires && (
                <div className="text-muted small">
                  Re-authorisation needed by {moment(status.refresh_token_expires).format("DD MMM YYYY")}
                </div>
              )}
            </>
          ) : (
            <div className="fw-semibold text-muted">
              <i className="ri-link-unlink me-1" />
              Not connected
            </div>
          )}
        </div>

        <div className="d-flex flex-wrap gap-2">
          <Button
            color={connected ? "light" : "primary"}
            onClick={handleConnect}
            disabled={Boolean(busy)}
          >
            {busy === "connect" && <Spinner size="sm" className="me-1" />}
            {connected ? "Reconnect" : "Connect to HMRC"}
          </Button>
          {connected && (
            <>
              <Button color="primary" outline onClick={handleTest} disabled={Boolean(busy)}>
                {busy === "test" && <Spinner size="sm" className="me-1" />}
                Test connection
              </Button>
              <Button
                color="danger"
                outline
                onClick={() => setConfirm({ kind: "disconnect" })}
                disabled={Boolean(busy)}
              >
                Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {testResult && (
        <Alert color={testResult.ok ? "success" : "danger"} className="mt-3 mb-0 py-2">
          {testResult.message}
        </Alert>
      )}

      <hr />

      {/* Live vs Test environment */}
      <div>
        <label className="form-label d-block mb-1">Environment</label>
        <ButtonGroup>
          <Button
            color={environment === "test" ? "primary" : "light"}
            onClick={() => requestEnvironment("test")}
            disabled={Boolean(busy)}
          >
            Test (Sandbox)
          </Button>
          <Button
            color={environment === "live" ? "primary" : "light"}
            onClick={() => requestEnvironment("live")}
            disabled={Boolean(busy) || !status?.live_configured}
          >
            Live
          </Button>
        </ButtonGroup>
        <p className="text-muted small mt-2 mb-0">
          Switching environment clears the current HMRC connection - you will need to reconnect.
        </p>
        {!status?.live_configured && (
          <Alert color="info" className="mt-2 mb-0 py-2">
            Live mode is not available yet. It unlocks once HMRC grants production access and the
            production credentials are configured.
          </Alert>
        )}
      </div>

      <AppModal
        open={confirm?.kind === "disconnect"}
        onClose={() => setConfirm(null)}
        title="Disconnect from HMRC?"
        loading={busy === "disconnect"}
        onSubmit={handleDisconnect}
        submitLabel="Disconnect"
        submitColor="error"
      >
        CRMit will forget its HMRC access. VAT and payroll RTI submissions will stop working until
        you connect again. You can also remove CRMit from your Government Gateway account.
      </AppModal>

      <AppModal
        open={confirm?.kind === "environment"}
        onClose={() => setConfirm(null)}
        title={`Switch HMRC to ${envLabel(confirm?.next)} mode?`}
        loading={busy === "environment"}
        onSubmit={() => handleSwitchEnvironment(confirm.next)}
        submitLabel="Switch"
      >
        Any existing connection belongs to the current environment and will be cleared - you will
        need to reconnect to HMRC.
      </AppModal>
    </Card>
  );
}

// Change #100747 - the HMRC tab now holds the connection plus the two filing screens.
const SECTIONS = [
  { id: "connection", label: "Connection" },
  { id: "vat", label: "VAT returns" },
  { id: "ct", label: "Corporation Tax" },
];

export default function HmrcIntegration() {
  const history = useHistory();
  const location = useLocation();
  const initial = new URLSearchParams(location.search).get("section");
  const [section, setSection] = useState(SECTIONS.some((s) => s.id === initial) ? initial : "connection");

  const choose = (id) => {
    setSection(id);
    const params = new URLSearchParams(location.search);
    if (id === "connection") params.delete("section");
    else params.set("section", id);
    const rest = params.toString();
    history.replace(`${location.pathname}${rest ? `?${rest}` : ""}`);
  };

  return (
    <>
      <ButtonGroup className="mb-2 flex-wrap">
        {SECTIONS.map((s) => (
          <Button key={s.id} color={section === s.id ? "primary" : "light"} onClick={() => choose(s.id)}>
            {s.label}
          </Button>
        ))}
      </ButtonGroup>
      {section === "connection" && <HmrcConnection />}
      {section === "vat" && <HmrcVatReturns />}
      {section === "ct" && <HmrcCorporationTax />}
    </>
  );
}
